import { getIsSharedVideoCompositionsEnabled } from '../constants/common';
import { SharedVideoCompositionInfo, VideoCompositionInfo } from '../types/obs';
import { promisefy } from '../utils/promisefy';
import { ScopedStorage } from './ScopedStorageService';

const SYNC_SHARED_COMPOSITIONS_LOCK_KEY = 'SharedVideoCompositionsService.syncSharedVideoCompositions';

//
// !!! ATTENTION !!!
//
// We do not call window.host.removeSharedVideoCompositions because the underlying OBS frontend API is broken.
//

const STORAGE_SHARED_CANVAS_MAPPING_HISTORY = {
    scope: 'streamelements_multi-streaming',
    container: 'shared_canvas_settings',
    item: 'preferred_mapping'
};

type SharedCanvasMapping = Record<string, { videoCompositionId: string }>;

async function getPreferredSharedVideoCompositionId({ videoCompositionId }: { videoCompositionId: string }): Promise<{ sharedVideoCompositionId: string } | undefined> {
    const storageItem = await ScopedStorage.readScopedStorageJsonItem<SharedCanvasMapping>(STORAGE_SHARED_CANVAS_MAPPING_HISTORY);

    const mapping = storageItem?.content;

    if (!mapping) return undefined;

    for (const sharedVideoCompositionId of Object.keys(mapping)) {
        const item = mapping[sharedVideoCompositionId];

        if (!item) continue;
            
        if (item.videoCompositionId === videoCompositionId) {
            return { sharedVideoCompositionId };
        }
    }

    return undefined;
}

async function setPreferredSharedVideoCompositionId({ sharedVideoCompositionId, videoCompositionId }: { sharedVideoCompositionId: string; videoCompositionId: string }): Promise<void> {
    const storageItem = await ScopedStorage.readScopedStorageJsonItem<SharedCanvasMapping>(STORAGE_SHARED_CANVAS_MAPPING_HISTORY);

    const mapping = storageItem?.content ?? {};

    if (mapping[sharedVideoCompositionId]?.videoCompositionId === videoCompositionId) {
        return;
    }

    mapping[sharedVideoCompositionId] = { videoCompositionId };

    await ScopedStorage.writeScopedStorageJsonItem({
        ...STORAGE_SHARED_CANVAS_MAPPING_HISTORY,
        content: mapping,
    });
}

export class SharedVideoCompositionsService {
    public static async unpublishVideoComposition(
        {
            videoComposition,
        }: 
        {
            videoComposition: VideoCompositionInfo;
        }
    ): Promise<{ sharedVideoCompositions: SharedVideoCompositionInfo[] } | undefined> {
        if (!await getIsSharedVideoCompositionsEnabled()) return undefined;

        return await navigator.locks.request(SYNC_SHARED_COMPOSITIONS_LOCK_KEY, async () => {
            const filterShared = Object.values(await promisefy(window.host.getAllSharedVideoCompositions)() ?? {}).filter(i => i.videoCompositionId === videoComposition.id);

            if (!filterShared.length) {
                // None assigned
                return undefined;
            }

            const disconnectResult = await promisefy(window.host.disconnectVideoCompositionsFromSharedVideoCompositionsByIds)(filterShared.map(i => i.id));

            if (!disconnectResult) {
                console.warn(
                    'SharedVideoCompositionsService.unpublishVideoComposition: window.host.disconnectVideoCompositionsFromSharedVideoCompositionsByIds call failed',
                    { videoComposition, sharedVideoCompositions: filterShared }
                );

                return undefined;
            }

            return { sharedVideoCompositions: filterShared };
        }) as { sharedVideoCompositions: SharedVideoCompositionInfo[] } | undefined;
    }

    public static async publishVideoComposition(
        {
            videoComposition,
            sharedVideoCompositionId,
        }: 
        {
            videoComposition: VideoCompositionInfo;
            sharedVideoCompositionId?: SharedVideoCompositionInfo['id'];
        }
    ): Promise<{ sharedVideoComposition: SharedVideoCompositionInfo } | undefined> {
        if (!await getIsSharedVideoCompositionsEnabled()) return undefined;

        return await navigator.locks.request(SYNC_SHARED_COMPOSITIONS_LOCK_KEY, async () => {
            const allShared = Object.values(await promisefy(window.host.getAllSharedVideoCompositions)() ?? {});

            const publishedShared = allShared.find(i => i.videoCompositionId === videoComposition.id);
            if (publishedShared) {
                // Already published

                await setPreferredSharedVideoCompositionId({ sharedVideoCompositionId: publishedShared.id, videoCompositionId: videoComposition.id });

                return { sharedVideoComposition: publishedShared };
            }

            const preferredSharedVideoCompositionId = sharedVideoCompositionId ?? (
                await getPreferredSharedVideoCompositionId({ videoCompositionId: videoComposition.id })
            )?.sharedVideoCompositionId;

            // Start by trying to match by requested SharedVideoComposition.id
            let freeShared: SharedVideoCompositionInfo | undefined | null = allShared.find(i => !i.videoCompositionId && i.id === preferredSharedVideoCompositionId);
            if (!freeShared) {
                // Try _any_ unassigned SharedVideoComposition
                freeShared = allShared.find(i => !i.videoCompositionId);
            }
            if (!freeShared) {
                // Create a new SharedVideoComposition
                freeShared = await promisefy(window.host.addSharedVideoComposition)({});

                if (!freeShared) {
                    console.warn(
                        'SharedVideoCompositionsService.publishVideoComposition: window.host.addSharedVideoComposition call failed',
                        { videoComposition }
                    );

                    return undefined;
                }
            }

            const connectedShared = await promisefy(window.host.connectVideoCompositionToSharedVideoComposition)({
                id: freeShared.id,
                videoCompositionId: videoComposition.id,
            });

            if (!connectedShared) {
                console.warn(
                    'SharedVideoCompositionsService.publishVideoComposition: window.host.connectVideoCompositionToSharedVideoComposition call failed',
                    { videoComposition, sharedVideoComposition: freeShared }
                );

                return undefined;
            }

            await setPreferredSharedVideoCompositionId({ sharedVideoCompositionId: freeShared.id, videoCompositionId: videoComposition.id });

            return { sharedVideoComposition: connectedShared };
        }) as { sharedVideoComposition: SharedVideoCompositionInfo } | undefined;
    }
}
